import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { AppCard, AppNoInfo, AppRemixIcon } from '@/layout/AppComponents';
import BreadcrumbHandler from '../../../layout/components/breadcrumb/BreadcrumbHandler';
import useIsMounted from '../../../layout/components/hooks/useIsMounted';
import errorHandler from '@/layout/components/error/ErrorHandler';
import { axiosPath, path } from '../../../routing/helpers/router';
import * as currentPageAction from '../../../store/actions/currentPageAction';
import Translator from '../../../layout/components/helpers/Translator';
import DocumentManagerPaginator from '../../../layout/screenSpecifics/Modules/DocumentManager/DocumentManagerPaginator';
import DocumentManagerSidebar from '../../../layout/screenSpecifics/Modules/DocumentManager/Sidebar/DocumentManagerSidebar';
import * as moduleAction from '../../../store/actions/moduleAction';

export default function ModuleDocumentManagerLogListScreen() {
  const { moduleBridgeHex, filtersHex, page } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const moduleData = useSelector((s) => s?.module?.[moduleBridgeHex]?.moduleData);
  const permissionsLevel = useSelector(
    (s) => s.module[moduleBridgeHex]?.userBridge?.permissions?.level,
  );
  const entries = useSelector((state) => state.currentPage?.entries);
  const pagination = useSelector((state) => state.currentPage?.pagination);
  const logsPage = useSelector((state) => state.currentPage?.logsPage);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    return () => {
      dispatch(currentPageAction.empty());
    };
  }, []);

  // > LOAD LOGS
  const loadLogs = useCallback(
    async () =>
      axios
        .post(axiosPath('api_r_user_module_documentManager_log_list'), {
          moduleBridgeHex,
          filtersHex,
          page,
        })
        .then(({ data }) => {
          if (isMounted.current) {
            dispatch(currentPageAction.change('pagination', data.pagination, true));
            dispatch(currentPageAction.change('entries', data.entries, true));
            dispatch(currentPageAction.change('logsPage', data.logsPage, true));
          }
        })
        .catch((e) => dispatch(errorHandler(e)))
        .finally(() => setLoading(false)),
    [moduleBridgeHex, filtersHex, page],
  );

  useEffect(() => {
    setLoading(true);
    loadLogs();
  }, [filtersHex, page]);
  // < LOAD LOGS

  // > LOAD MODULE DATA //
  const loadModuleData = useCallback(async () => {
    dispatch(
      moduleAction.fetch(
        axiosPath('api_r_user_module_documentManager_getModuleData'),
        'moduleData',
        {
          moduleBridgeHex,
        },
      ),
    );
  }, []);

  useEffect(() => {
    loadModuleData();
  }, []);
  // > LOAD MODULE DATA //

  // < STORE CALLS
  const loadModulePermissions = useCallback(async () => {
    try {
      await dispatch(
        moduleAction.fetch(
          axiosPath('api_r_user_module_documentManager_getPermissions'),
          'permissions',
          {
            moduleBridgeHex,
            subType: 'permissions',
          },
        ),
      );
      await dispatch(
        moduleAction.fetch(
          axiosPath('api_r_user_module_documentManager_getPermissions'),
          'userBridge',
          {
            moduleBridgeHex,
            subType: 'userBridge',
          },
        ),
      );
    } catch (err) {}
  }, [moduleBridgeHex]);

  useEffect(() => {
    loadModulePermissions();
  }, [moduleBridgeHex]);

  if (!moduleData) return <AppNoInfo />;

  if (permissionsLevel === 0) {
    return (
      <>
        <DocumentManagerSidebar activeRoute="user_module_documentManager_log_list" />
        <BreadcrumbHandler
          paths={
            moduleData
              ? [
                  {
                    name: `Repositorio ${
                      permissionsLevel === 0 ? '(ADMINISTRADOR)' : ''
                    }`,
                    route: {
                      name: 'user_module_documentManager_item_list',
                      params: {
                        moduleBridgeHex,
                        parentItemHex: '0',
                        page: '1',
                        filtersHex: '0',
                      },
                    },
                  },
                  {
                    name: 'Logs',
                  },
                ]
              : [{ name: '...' }]
          }
        />
        <main role="main" className="col-md-12 col-lg-12 px-md-4 mb-5">
          <div className="justify-content-between flex-wrap flex-md-nowrap align-items-center pt-0 pb-0 mb-0">
            {filtersHex !== '0' && (
              <div className="alert alert-info pointer">
                <span
                  onClick={() =>
                    navigate(
                      path({
                        name: 'user_module_documentManager_log_list',
                        params: {
                          moduleBridgeHex,
                          page: 1,
                          filtersHex: '0',
                        },
                      }),
                    )
                  }
                >
                  <AppRemixIcon icon="delete-bin-6" className="mr-2" />
                  <Translator file="filter" name="filter.delete" />
                </span>
              </div>
            )}
            {loading ? (
              <AppNoInfo minHeight={window.innerHeight - 60} />
            ) : entries.length > 0 ? (
              <AppCard
                header={
                  <Translator file="module-legalCompliance" name="historical.state" />
                }
                className="mb-2"
              >
                <div className="table-responsive">
                  <table className="table">
                    <thead>
                      <tr>
                        <th className="col-2">Fecha</th>
                        <th className="col-2">Usuario</th>
                        <th className="col-8">Descripción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entries?.map((l) => (
                        <tr key={l.id}>
                          <td>{l?.dateStamp}</td>
                          <td>{l?.user}</td>
                          <td>{l?.description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {pagination?.pageCount > 1 && (
                  <DocumentManagerPaginator
                    pagination={pagination}
                    route="user_module_documentManager_log_list"
                    page={page}
                    plantObligationPages={logsPage}
                    itemsPerPage={20}
                    className="mt-3"
                    params={{
                      moduleBridgeHex,
                      page,
                      filtersHex,
                    }}
                  />
                )}
              </AppCard>
            ) : (
              <div className="alert alert-info">Aún no existe ningún registro</div>
            )}
          </div>
        </main>
      </>
    );
  } else {
    return <></>;
  }
}
